import React, { useState, useEffect, useRef } from 'react';

import { AxiosError } from 'axios';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { DotBricksLoading, Toast } from 'src/common/components';
import { useAuth } from 'src/common/hooks';
import { getUserInitial } from 'src/common/utils';

import { fetchAccount, updateUser } from './api';
import { AccountSearch } from './components';
import { EMAIL_PATTERN } from './constants';

type UserFormType = {
  available_accounts_for_switch: Array<{ id: string; name: string }>;
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  confirm_password?: string;
  is_account_manager: boolean;
  can_switch_accounts: boolean;
  account: string;
  accountPayload: { name: string; id: string };
  username: string;
};

const Main: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { connectedUser, updateConnectedUser } = useAuth();
  const navigate = useNavigate();
  const isAccountFetched = useRef(false);
  const methods = useForm<UserFormType>({ defaultValues: { ...connectedUser, password: '' } });
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
    watch,
    setError,
  } = methods;
  const { username } = connectedUser;
  const { first_name: firstName, last_name: lastName, password } = watch();
  const userInitial = getUserInitial({ first_name: firstName, last_name: lastName, username });

  const handleUpdateUser = async (user: UserFormType): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: Record<string, any> = {
      ...user,
      available_accounts_for_switch:
        user.available_accounts_for_switch?.map((acc) => acc?.id) ?? [],
      account: user.accountPayload.id,
    };
    try {
      setIsSaving(true);
      // if password is empty than don't consider it.
      if (password?.length === 0) {
        delete payload.password;
      }
      // Only send boolean flag fields if the user explicitly changed them,
      // to avoid overwriting values that may have been updated elsewhere
      // (e.g., via Cosmos admin).
      if (!dirtyFields.can_switch_accounts) {
        delete payload.can_switch_accounts;
      }
      if (!dirtyFields.is_account_manager) {
        delete payload.is_account_manager;
      }
      await updateUser(connectedUser.id, payload as Parameters<typeof updateUser>[1]);
      updateConnectedUser({ ...connectedUser, ...user });
      Toast.success('The user was updated successfully.');
    } catch (err) {
      const error = err as AxiosError<{ username: string[]; password: string[] }>;
      const dataError = error.response?.data;
      if (dataError?.username) {
        setError('username', { type: 'server', message: dataError.username[0] });
      }
      if (dataError?.password) {
        setError('password', { type: 'server', message: dataError.password[0] });
      }
      Toast.error('There was an error updating the user.');
    } finally {
      setIsSaving(false);
    }
  };

  const validatePassword = (value: string | undefined): boolean => {
    if (!password) return true;
    if (password !== value) return false;
    return true;
  };

  const handleBack = (): void => {
    navigate('/');
  };

  useEffect(() => {
    const fetch = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const { data } = await fetchAccount(connectedUser.account);
        isAccountFetched.current = true;
        reset({
          ...connectedUser,
          password: '',
          confirm_password: '',
          account: data.id,
          accountPayload: { name: data.name, id: data.id },
        });
      } catch (err) {
        setError('accountPayload', {
          type: 'server',
          message: 'There was an error fetching the current account.',
        });
      } finally {
        setIsLoading(false);
      }
    };
    if (connectedUser.account && !isAccountFetched.current) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetch();
    }
  }, [connectedUser, reset, setError]);

  if (isLoading) {
    return <DotBricksLoading />;
  }

  return (
    <FormProvider {...methods}>
      <form
        className="sm:p-8 flex flex-col h-full p-6 overflow-auto"
        onSubmit={handleSubmit(handleUpdateUser)}
        data-testid="connected-user-form"
      >
        <h2 className="mt-1">Update user</h2>
        <div className="flex my-8">
          <div className="flex w-12 h-12 rounded-full bg-primary-1.0">
            <h3 className="m-auto text-white-1.0">{userInitial}</h3>
          </div>
          <p className="text-gray-1.0 font-semibold my-auto ml-2">User:</p>
          <p className="text-gray-1.0 font-semibold ml-2 my-auto">{username || 'Not filled yet'}</p>
        </div>
        <label htmlFor="username">
          <p className="input-label">Username</p>
          <p className="sm:w-1/2 oversize w-full mt-1 transition duration-150 ease-in-out">
            {connectedUser.username}
          </p>
        </label>
        <label htmlFor="first_name" className="mt-4">
          <p className="input-label">First name</p>
          <input
            id="first_name"
            className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
            {...register('first_name', { required: true })}
            defaultValue=""
            data-testid="first-name"
            autoComplete="off"
          />
          {errors.first_name && <p className="error-text"> The first name is required</p>}
        </label>
        <label htmlFor="last_name" className="mt-4">
          <p className="input-label">Last name</p>
          <input
            id="last_name"
            className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
            {...register('last_name', { required: true })}
            defaultValue=""
            data-testid="last-name"
            autoComplete="off"
          />
          {errors.last_name && <p className="error-text"> The last name is required</p>}
        </label>
        <label htmlFor="email" className="mt-4">
          <p className="input-label">Email</p>
          <input
            id="email"
            className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
            {...register('email', { required: true, pattern: EMAIL_PATTERN })}
            defaultValue=""
            data-testid="email"
            autoComplete="off"
          />
          {errors.email && errors.email.type === 'required' && (
            <p className="error-text"> The email is required</p>
          )}
          {errors.email && errors.email.type === 'pattern' && (
            <p className="error-text">Please enter a valid email</p>
          )}
        </label>
        <AccountSearch />
        <label htmlFor="password" className="mt-4">
          <p className="input-label">Password</p>
          <input
            id="password"
            className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
            {...register('password')}
            defaultValue=""
            data-testid="password"
            autoComplete="off"
            type="password"
          />
          {errors.password && <p className="error-text"> The password is required</p>}
        </label>
        <label htmlFor="confirm_password" className="mt-4">
          <p className="input-label">Confirm password</p>
          <input
            id="confirm_password"
            className="input sm:w-1/2 w-full mt-1 transition duration-150 ease-in-out"
            {...register('confirm_password', {
              required: password && password.length > 0,
              validate: validatePassword,
            })}
            defaultValue=""
            data-testid="confirm-password"
            autoComplete="off"
            type="password"
          />
          {errors.confirm_password && errors.confirm_password.type === 'required' && (
            <p className="error-text"> The confirm password is required</p>
          )}
          {errors.confirm_password && errors.confirm_password.type === 'validate' && (
            <p className="ml-1 small font-semibold text-red-1.0">Password does not match</p>
          )}
        </label>
        <label htmlFor="is_account_manager" className=" mt-4">
          <div className="flex">
            <p className="input-label">Is account manager?</p>
            <input
              id="is_account_manager"
              className="my-auto ml-4 transition duration-150 ease-in-out"
              {...register('is_account_manager')}
              data-testid="is-account-manager"
              type="checkbox"
            />
          </div>
          <p className="helper-text">True if the user is an admin for this account.</p>
        </label>
        <label htmlFor="can_switch_accounts" className=" mt-4">
          <div className="flex">
            <p className="input-label">Can switch accounts?</p>
            <input
              id="can_switch_accounts"
              className="my-auto ml-4 transition duration-150 ease-in-out"
              {...register('can_switch_accounts')}
              data-testid="can-switch-accounts"
              type="checkbox"
            />
          </div>
          <p className="helper-text">True if the user is allowed to change between accounts.</p>
        </label>
        <div className="flex mt-8 ml-auto">
          <button className="btn btn-ghost" type="button" onClick={handleBack} disabled={isSaving}>
            Back
          </button>
          <button
            className="btn btn-secondary ml-4"
            type="submit"
            disabled={isSaving}
            data-testid="save-user"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

export default Main;
